const KEY_PREFIX = 'group-life-clone-key:';

/** O que está sendo clonado — cada tipo tem a própria chave e o próprio aviso. */
export type GroupLifeCloneKind = 'QUOTE' | 'PROPOSAL' | 'ALTER';

/**
 * Status em que a clonagem PODE ter sido concluída no servidor sem a resposta
 * chegar: o API Gateway corta em 30s (502/503/504) enquanto a API continua, e
 * o 409 é a própria API avisando que o clone com esta chave ainda roda. Sem
 * status = falha de rede, também sem saber se a requisição chegou.
 */
const PENDING_STATUSES = new Set([409, 502, 503, 504]);

export function isClonePendingStatus(status: number | undefined): boolean {
  return status === undefined || PENDING_STATUSES.has(status);
}

const PENDING_LIST_LABEL: Record<GroupLifeCloneKind, string> = {
  QUOTE: 'a lista de cotações',
  PROPOSAL: 'a lista de propostas',
  ALTER: 'a lista de propostas',
};

/** Erro de clonagem cujo desfecho no servidor é desconhecido ou ainda em curso. */
export class GroupLifeClonePendingError extends Error {
  constructor(kind: GroupLifeCloneKind = 'QUOTE') {
    super(
      `A clonagem está demorando mais que o normal e continua em processamento. Confira ${PENDING_LIST_LABEL[kind]} em instantes.`
    );
    this.name = 'GroupLifeClonePendingError';
  }
}

const memoryKeys = new Map<string, string>();

function getStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function generateKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `clone-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
}

function toStorageKey(
  sourcePolicyId: string,
  kind: GroupLifeCloneKind
): string {
  return `${KEY_PREFIX}${kind}:${sourcePolicyId}`;
}

/**
 * Chave de idempotência da clonagem desta cotação/proposta. É a mesma em toda
 * tentativa até uma dar certo — repetir depois de um timeout devolve o clone
 * já criado (ou 409 enquanto ele roda) em vez de gerar outro.
 */
export function getCloneIdempotencyKey(
  sourcePolicyId: string,
  kind: GroupLifeCloneKind = 'QUOTE'
): string {
  const storageKey = toStorageKey(sourcePolicyId, kind);
  try {
    const stored = getStorage()?.getItem(storageKey);
    if (stored) return stored;
  } catch {
    // sessionStorage bloqueado: segue com a memória.
  }

  const key = memoryKeys.get(storageKey) ?? generateKey();
  memoryKeys.set(storageKey, key);
  try {
    getStorage()?.setItem(storageKey, key);
  } catch {
    // idem
  }
  return key;
}

/** Descarta a chave depois do clone concluído: a próxima clonagem é outra intenção. */
export function clearCloneIdempotencyKey(
  sourcePolicyId: string,
  kind: GroupLifeCloneKind = 'QUOTE'
): void {
  const storageKey = toStorageKey(sourcePolicyId, kind);
  memoryKeys.delete(storageKey);
  try {
    getStorage()?.removeItem(storageKey);
  } catch {
    // idem
  }
}
